import { DatePipe } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CoilSheet, CoilSheetDataSets } from '../../shared/entities/coilSheet';
import { ExportData } from '../../shared/entities/export';
import { ExportService } from '../../shared/services/export.service';
import { Subscription } from 'rxjs';
import { EventEmitterService } from '../../shared/services/event-emitter.service';
import { MeasurementsDataService } from '../../shared/services/measurements-data.service';
import { Utility } from '../../shared/entities/utils';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-export-dialog',
  standalone: true,
  imports: [
    ButtonModule,
    DialogModule,
    FormsModule
  ],
  providers: [DatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './export-dialog.component.html',
  styleUrl: './export-dialog.component.scss'
})
export class ExportDialogComponent {
  regionUnit: string = '';
  constructor(
    private toastr: ToastrService,
    private exportService: ExportService,
    private datepipe: DatePipe,
    private spinner: NgxSpinnerService,
    private eventEmitterService: EventEmitterService,
    private measurementsDataService: MeasurementsDataService) {
    this.measurementsDataService.getMeasurementsData.subscribe(unit => this.regionUnit = unit);
  }

  @ViewChild('graphKeyToExport') graphKeyToExport: ElementRef | undefined;
  @ViewChild('exportDataFormat') exportDataFormat: ElementRef | undefined;

  @Input() showExportDialog: boolean = false;
  @Output() displayChange = new EventEmitter();

  exportData: ExportData | undefined;

  //this will be all available graps ( not on the UI dropdown extra will be there)
  availableGraphs: { key: string; label: string; }[] = [];;
  coilSheetDataSetsToExport: CoilSheetDataSets[] = [];
  exportDataFormats: string[] = ['CSV', 'PDF', 'EXCEL'];

  graphLabels: { [key: string]: string } = {
    WIDTH_GRAPH: "Width",
    THICKNESS_GRAPH: "Thickness",
    CROWN_GRAPH: "Crown",
    WEDGE_GRAPH: "Wedge"
  };

  private exportDataSubscription: Subscription | undefined;

  ngOnInit(): void {
    this.exportDataSubscription = this.eventEmitterService.get<ExportData>('exportData').subscribe((exportData: ExportData) => {
      this.exportData = exportData;
      this.populateAvailableGraphs();
    });
  }

  ngOnDestroy() {
    if (this.exportDataSubscription) {
      this.exportDataSubscription.unsubscribe();
    }
  }
  //iterate over all the coilsheets and find all unique graph types
  private populateAvailableGraphs(): void {
    this.availableGraphs = [];
    this.exportData?.coilSheetsToExport.forEach(coilSheet => {
      if (coilSheet.availableDataSetNames) {
        coilSheet.availableDataSetNames.forEach((key: string) => {
          if (!this.availableGraphs.find(graph => graph.key === key)) {
            this.availableGraphs.push({ key: key, label: this.graphLabels[key] });
          }
        });
      }
    });
  }

  onExportDataDialogButtonClicked() {
    const selectedGraphKeyToExport = this.graphKeyToExport?.nativeElement.value;
    const selectedExportDataFormat = this.exportDataFormat?.nativeElement.value;

    let filteredCoilSheetDataSetsToExport: CoilSheetDataSets[] = this.getFilteredCoilSheetDataSetsToExport(selectedGraphKeyToExport, this.exportData?.coilSheetsToExport);
    this.doExport(filteredCoilSheetDataSetsToExport, selectedGraphKeyToExport, selectedExportDataFormat);
  }

  getFilteredCoilSheetDataSetsToExport(selectedGraphKeyToExport: string, coilSheetsToExport: CoilSheet[] | undefined): CoilSheetDataSets[] {
    coilSheetsToExport = coilSheetsToExport ?? [];
    coilSheetsToExport.forEach(element => {
      element?.coilSheetDataSets?.forEach(elementInner => {
        elementInner.coilSheetId = element.coilSheetId
      });
    });
    const filteredCoilSheetDataSetsToExport: CoilSheetDataSets[] = [];
    coilSheetsToExport.forEach(coilSheet => {
      if (coilSheet.coilSheetDataSets && coilSheet.coilSheetDataSets.length > 0) {
        coilSheet.coilSheetDataSets.forEach(dataset => {
          if (dataset.name === selectedGraphKeyToExport) {
            //deep copying object
            filteredCoilSheetDataSetsToExport.push(structuredClone(dataset));
          }
        });
      }
    });

    return filteredCoilSheetDataSetsToExport;
  }

  doExport(filteredCoilSheetDataSetsToExport: CoilSheetDataSets[], selectedGraphKeyToExport: string, selectedExportDataFormat: string) {
    let convertedData: any = [];

    filteredCoilSheetDataSetsToExport.forEach(element => {
      convertedData.push(Utility.convertToGraph(JSON.stringify(element.dataSet) || ''));
    })
    convertedData.forEach((elementConverted: any, index: any) => {
      if (this.regionUnit === 'US_CUSTOMARY_UNITS') {
        convertedData[index].values = convertedData[index].values.map((item: number) => Utility.convertMillimeterToInch(item));
        convertedData[index].upperLimit = Utility.convertMillimeterToInch(convertedData[index].upperLimit);
        convertedData[index].lowerLimit = Utility.convertMillimeterToInch(convertedData[index].lowerLimit);
        filteredCoilSheetDataSetsToExport[index].dataSet = convertedData[index];
      }
    })

    if (filteredCoilSheetDataSetsToExport && filteredCoilSheetDataSetsToExport.length > 0) {
      this.spinner.show();

      this.exportService.exportCoilSheet(filteredCoilSheetDataSetsToExport, selectedExportDataFormat, selectedGraphKeyToExport, this.regionUnit).subscribe({
        next: async (res) => {
          let fileName: string = `${this.datepipe.transform(new Date(), 'yyyyMMdd')}_${selectedExportDataFormat}_${this.exportData?.exportFrom}_${this.exportData?.exportId}`;

          this.exportService.downloadFile(res, fileName);
          this.spinner.hide();
          this.showExportDialog = false;
          this.displayChange.emit(false);
        }, error: (error: any) => {
          this.spinner.hide();
          this.toastr.error('Error in export');
        }
      });
    }
  }

  onClose() {
    this.displayChange.emit(false);
    this.showExportDialog = false;
  }
}
